// src/components/PwaInstall.tsx
// Aplica la marca de la org a la app instalable y ofrece instalarla:
// - Chrome / Edge / Android: botón «Instalar» (evento beforeinstallprompt).
// - iPhone / iPad (Safari no tiene ese evento): instrucción
//   Compartir → «Agregar a pantalla de inicio».
// Se descarta una vez y no vuelve a salir en ese navegador.

import { useEffect, useState } from 'react';
import { Download, Share, X } from 'lucide-react';
import { useBranding } from '@/context/BrandingContext';
import { useDashboardShell } from '@/context/DashboardShellContext';
import { shellBrand } from '@/modules/orion-shell/brand';
import { aplicarMarcaPwa, esIosSafari, esStandalone } from '@/lib/pwa';

const CLAVE_DESCARTE = 'orion:pwa:aviso-descartado';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function leerDescarte(): boolean {
  try {
    return localStorage.getItem(CLAVE_DESCARTE) === '1';
  } catch {
    return false;
  }
}

export default function PwaInstall() {
  const { orgName: nombreOrg, orgLogo, palette } = useBranding();
  const { config } = useDashboardShell();
  const { appIcon, appShortName } = shellBrand(config);
  // Dentro de la app el nombre de /org/info puede no llegar (p. ej. municipio-slp): se usa
  // el de la marca del shell (dashboard.brand.name). Sin ninguno, no se toca el manifest.
  const nombreMarca = typeof config?.brand?.name === 'string' ? config.brand.name.trim() : '';
  const orgName = nombreOrg || nombreMarca;
  const [evento, setEvento] = useState<BeforeInstallPromptEvent | null>(null);
  const [descartado, setDescartado] = useState(leerDescarte);

  useEffect(() => {
    if (!orgName) return;
    aplicarMarcaPwa({ nombre: orgName, logo: orgLogo, fondo: palette.bg_light, tema: palette.bg_dark,
      icono: appIcon, nombreCorto: appShortName });
  }, [orgName, orgLogo, palette.bg_light, palette.bg_dark, appIcon, appShortName]);

  useEffect(() => {
    const alOfrecer = (e: Event) => {
      e.preventDefault();
      setEvento(e as BeforeInstallPromptEvent);
    };
    const alInstalar = () => setEvento(null);
    window.addEventListener('beforeinstallprompt', alOfrecer);
    window.addEventListener('appinstalled', alInstalar);
    return () => {
      window.removeEventListener('beforeinstallprompt', alOfrecer);
      window.removeEventListener('appinstalled', alInstalar);
    };
  }, []);

  const descartar = () => {
    setDescartado(true);
    try {
      localStorage.setItem(CLAVE_DESCARTE, '1');
    } catch {
      // sin storage: solo se oculta en esta sesión
    }
  };

  const instalar = async () => {
    if (!evento) return;
    await evento.prompt();
    await evento.userChoice.catch(() => null);
    setEvento(null);
  };

  if (descartado || !orgName || esStandalone()) return null;
  const ios = esIosSafari();
  if (!evento && !ios) return null;

  return (
    <div
      role="dialog"
      aria-label={`Instalar ${orgName}`}
      className="fixed inset-x-3 bottom-3 z-50 mx-auto flex max-w-md items-center gap-3 rounded-2xl border border-border bg-card p-3 text-card-foreground shadow-lg sm:inset-x-auto sm:right-4 sm:bottom-4"
      style={{ marginBottom: 'env(safe-area-inset-bottom)' }}
    >
      {orgLogo ? (
        <img src={orgLogo} alt="" className="h-10 w-10 shrink-0 rounded-xl bg-muted object-contain p-1" />
      ) : (
        <img src="/icons/icon-192.png" alt="" className="h-10 w-10 shrink-0 rounded-xl" />
      )}
      <div className="min-w-0 flex-1 text-sm">
        <p className="font-medium">Instala {orgName} en tu dispositivo</p>
        {ios ? (
          <p className="text-muted-foreground">
            Toca <Share className="inline h-3.5 w-3.5 -translate-y-px" aria-label="Compartir" /> y luego
            «Agregar a pantalla de inicio».
          </p>
        ) : (
          <p className="text-muted-foreground">Ábrela como app, sin pestañas del navegador.</p>
        )}
      </div>
      {!ios && (
        <button
          type="button"
          onClick={instalar}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
        >
          <Download className="h-4 w-4" /> Instalar
        </button>
      )}
      <button
        type="button"
        onClick={descartar}
        aria-label="Cerrar"
        className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
